import { NotImplementedError, ServiceStatus, Validation } from "../../../types";
import { ObservableHandler } from "../../../handlers/observable";
import { PublisherGroupSaveFailed, PublisherGroupFetchFailed } from "./errors";
import { filtersHasEmptyIdParam, parseIdFilter } from "../../../handlers/filter";
import { isServicePage } from "../../../handlers/page";
import axios from "axios";
import { Observable } from "rxjs";
import { serviceListToList, listToServiceList } from "../../../models/publisher";
import { validators } from "./validators";
import { toQueryParams } from "../utils/query";
import Audits from "../audits";
class PublisherGroups extends ObservableHandler {
    constructor(sdk) {
        super(sdk, "publisher-groups", { atomize: true });
        this.toPublisherGroup = (group) => {
            return serviceListToList(group);
        };
        this.url = `${this.sdk.urls.baseAPIUrl}/publisher-groups`;
        this.urlSingle = `${this.sdk.urls.baseAPIUrl}/publisher-group`;
        this.validators = validators(sdk);
        this.audits = new Audits(sdk);
    }
    /**
     * Find publisher group(s) using filters.
     * @param filters - filters to determine what publisher group(s) to get
     * @param sort - optional sort configuration
     * @returns Observable yielding the requested publisher group(s)
     */
    findItems(filters, sort) {
        return new Observable((subscriber) => {
            if (filtersHasEmptyIdParam(filters.where)) {
                subscriber.next([]);
                subscriber.complete();
                return;
            }
            const idFilter = parseIdFilter(filters);
            const promise = idFilter.size === 1
                ? this.getPublisherGroup(idFilter.values().next().value).then((group) => [
                    group
                ])
                : this.getPublisherGroups(filters, sort);
            promise
                .then((groups) => {
                subscriber.next(groups);
                subscriber.complete();
            })
                .catch((error) => {
                subscriber.error(error);
            });
        });
    }
    async getPublisherGroups(filters = {}, sort) {
        const params = toQueryParams(filters, sort);
        const pageSize = filters?.paging?.size || 10;
        return this.cache.promise(`${this.url}${params}`, () => axios
            .get(`${this.url}${params}`, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => {
            const groups = res.data?.map(this.toPublisherGroup) || [];
            let results = groups;
            if (isServicePage(res)) {
                results = {
                    page: {
                        count: res.paging_info?.count,
                        token: res.paging_info?.token,
                        size: pageSize
                    },
                    data: groups
                };
            }
            return results;
        })
            .catch((error) => {
            throw new PublisherGroupFetchFailed(error);
        }));
    }
    async getPublisherGroup(id) {
        return this.cache.promise(`${this.urlSingle}/${id}`, () => axios
            .get(`${this.urlSingle}/${id}`, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => this.toPublisherGroup(res?.data))
            .catch((error) => {
            throw new PublisherGroupFetchFailed(error);
        }));
    }
    /**
     * Save a publisher group (create or update).
     * Uses same POST endpoint for both operations.
     */
    saveItem(data, options) {
        const { skipValidation } = options || {};
        return new Observable((observer) => {
            const validationResult = skipValidation ? new Validation() : this.validate(data);
            Promise.resolve(validationResult)
                .then((errors) => {
                if (errors.size) {
                    throw errors;
                }
                return axios.post(this.urlSingle, listToServiceList(data), {
                    headers: {
                        "Content-Type": "application/json"
                    }
                });
            })
                .then(({ data: res }) => {
                const saved = this.toPublisherGroup(res.data);
                this.cache.reconcile("save", saved);
                observer.next(saved);
                observer.complete();
            })
                .catch((error) => {
                if (error instanceof Validation) {
                    observer.error(new Error(error.values().next().value));
                    return;
                }
                observer.error(new PublisherGroupSaveFailed(error));
            });
        });
    }
    /**
     * Creates a new publisher group with defaults.
     */
    make(defaults) {
        const defaultGroup = {
            id: "",
            name: "",
            cap: 0,
            updated: new Date(),
            publishers: [],
            status: ServiceStatus.DRAFT,
            updatedBy: "",
            version: 0,
            ...defaults
        };
        return Promise.resolve(defaultGroup);
    }
    async deleteItem() {
        return new Promise((_, reject) => {
            reject(new NotImplementedError("delete"));
        });
    }
}
export default PublisherGroups;
//# sourceMappingURL=index.js.map